import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Plus, Wand2, Info, Beaker, Pencil } from "lucide-react";
import { ensurePayrollPhase3Bootstrap } from "../payrollService";
import { useCompany } from "../../context/CompanyContext";
import {
  getEmployees,
  getStatutoryRules,
  getStatutoryRuleById,
  createStatutoryRule,
  updateStatutoryRule,
  getEmployeeStatutoryProfiles,
  upsertEmployeeStatutoryProfile,
  testPayrollRule,
} from "../payrollService";

const NIGERIA_PIT_PRESET = [
  { up_to: 800000, rate: 0 },
  { next: 2200000, rate: 15 },
  { next: 9000000, rate: 18 },
  { next: 13000000, rate: 21 },
  { next: 25000000, rate: 23 },
  { over: 50000000, rate: 25 },
];

const PRESETS = {
  nigeria_pit_2026: {
    label: "Nigeria PIT Progressive Preset",
    rule_code: "PAYE",
    rule_name: "Nigeria PAYE Progressive Tax",
    rule_type: "tax",
    calculation_method: "progressive",
    applies_to: "taxable_gross",
    config: {
      bands: NIGERIA_PIT_PRESET,
    },
  },
  pension_employee_8: {
    label: "Employee Pension 8%",
    rule_code: "PENSION_EMPLOYEE",
    rule_name: "Employee Pension Contribution",
    rule_type: "pension_employee",
    calculation_method: "percentage",
    applies_to: "pensionable_gross",
    config: {
      percentage: 8,
    },
  },
  pension_employer_10: {
    label: "Employer Pension 10%",
    rule_code: "PENSION_EMPLOYER",
    rule_name: "Employer Pension Contribution",
    rule_type: "pension_employer",
    calculation_method: "percentage",
    applies_to: "pensionable_gross",
    config: {
      percentage: 10,
    },
  },
  nhf_2_5: {
    label: "NHF 2.5% of Basic",
    rule_code: "NHF",
    rule_name: "National Housing Fund",
    rule_type: "nhf",
    calculation_method: "percentage",
    applies_to: "basic",
    config: {
      percentage: 2.5,
    },
  },
};

const initialRuleForm = {
  preset_key: "",
  rule_code: "",
  rule_name: "",
  rule_type: "tax",
  calculation_method: "percentage",
  applies_to: "gross",
  active: true,
  effective_from: "",
  effective_to: "",
  percentage: "",
  amount: "",
  bands_json: "",
  formula: "",
};

const initialProfileForm = {
  employee_id: "",
  tax_state: "",
  tax_residency: "",
  pension_exempt: false,
  tax_exempt: false,
  nhf_exempt: false,
  nhis_exempt: false,
};

function pickOne(value) {
  if (!value) return null;
  return Array.isArray(value) ? value[0] || null : value;
}

function safeParseBands(input) {
  if (!input?.trim()) return [];
  try {
    const parsed = JSON.parse(input);
    if (!Array.isArray(parsed)) {
      throw new Error("Bands JSON must be an array");
    }
    return parsed;
  } catch {
    throw new Error("Bands JSON is invalid. Use a valid JSON array.");
  }
}

function presetToForm(presetKey) {
  const preset = PRESETS[presetKey];
  if (!preset) return initialRuleForm;

  return {
    ...initialRuleForm,
    preset_key: presetKey,
    rule_code: preset.rule_code,
    rule_name: preset.rule_name,
    rule_type: preset.rule_type,
    calculation_method: preset.calculation_method,
    applies_to: preset.applies_to,
    percentage:
      preset.calculation_method === "percentage"
        ? String(preset.config?.percentage ?? "")
        : "",
    amount:
      preset.calculation_method === "fixed"
        ? String(preset.config?.amount ?? "")
        : "",
    bands_json:
      preset.calculation_method === "progressive"
        ? JSON.stringify(preset.config?.bands || [], null, 2)
        : "",
    formula:
      preset.calculation_method === "formula"
        ? String(preset.config?.formula || "")
        : "",
  };
}

export default function PayrollCompliance() {
  const { companyId } = useCompany();

  const [busy, setBusy] = useState(true);
  const [msg, setMsg] = useState("");

  const [employees, setEmployees] = useState([]);
  const [rules, setRules] = useState([]);
  const [profiles, setProfiles] = useState([]);

  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const [ruleForm, setRuleForm] = useState(initialRuleForm);
  const [profileForm, setProfileForm] = useState(initialProfileForm);

  const initialRuleTestValues = {
    gross: "",
    basic: "",
    taxable_gross: "",
    pensionable_gross: "",
    net: "",
    regular_hours: "",
    overtime_hours: "",
  };

  const [testingRule, setTestingRule] = useState(false);
  const [ruleTestValues, setRuleTestValues] = useState(initialRuleTestValues);
  const [ruleTestResult, setRuleTestResult] = useState(null);

  const [editingRuleId, setEditingRuleId] = useState("");
  const [loadingRuleForEdit, setLoadingRuleForEdit] = useState(false);
  const load = useCallback(async () => {
    if (!companyId) return;

    setBusy(true);
    setMsg("");

    try {
      await ensurePayrollPhase3Bootstrap(companyId);

      const [employeeData, rulesData, profilesData] = await Promise.all([
        getEmployees(companyId),
        getStatutoryRules(companyId),
        getEmployeeStatutoryProfiles(companyId),
      ]);

      setEmployees(employeeData || []);
      setRules(rulesData || []);
      setProfiles(profilesData || []);
    } catch (err) {
      setMsg(err.message || "Failed to load compliance data");
    } finally {
      setBusy(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const activeRules = useMemo(
    () => rules.filter((x) => x.active).length,
    [rules]
  );

  const exemptProfiles = useMemo(
    () =>
      profiles.filter(
        (x) => x.tax_exempt || x.pension_exempt || x.nhf_exempt || x.nhis_exempt
      ).length,
    [profiles]
  );

  async function handleSaveRule(e) {
    e.preventDefault();

    try {
      setMsg("");

      const config = {};

      if (ruleForm.calculation_method === "fixed") {
        config.amount = Number(ruleForm.amount || 0);
      }

      if (ruleForm.calculation_method === "percentage") {
        config.percentage = Number(ruleForm.percentage || 0);
      }

      if (ruleForm.calculation_method === "progressive") {
        config.bands = safeParseBands(ruleForm.bands_json);
      }

      if (ruleForm.calculation_method === "formula") {
        config.formula = String(ruleForm.formula || "").trim();
        if (!config.formula) {
          throw new Error("Formula cannot be empty");
        }
      }

      const payload = {
        company_id: companyId,
        rule_code: ruleForm.rule_code.trim().toUpperCase(),
        rule_name: ruleForm.rule_name.trim(),
        rule_type: ruleForm.rule_type,
        calculation_method: ruleForm.calculation_method,
        applies_to: ruleForm.applies_to,
        active: !!ruleForm.active,
        effective_from:
          ruleForm.effective_from || new Date().toISOString().slice(0, 10),
        effective_to: ruleForm.effective_to || null,
        config,
      };

      if (editingRuleId) {
        await updateStatutoryRule(editingRuleId, payload);
        setMsg("Statutory rule updated successfully.");
      } else {
        await createStatutoryRule(payload);
        setMsg("Statutory rule created successfully.");
      }

      resetRuleModalState();
      setShowRuleModal(false);
      await load();
    } catch (err) {
      setMsg(err.message || "Failed to save statutory rule");
    }
  }

  async function handleToggleRule(row) {
    try {
      setMsg("");

      await updateStatutoryRule(row.id, {
        active: !row.active,
      });

      await load();
      setMsg(
        `Rule "${row.rule_name}" has been ${
          row.active ? "deactivated" : "activated"
        }.`
      );
    } catch (err) {
      setMsg(err.message || "Failed to update statutory rule");
    }
  }

  function resetRuleModalState() {
    setRuleForm(initialRuleForm);
    setRuleTestValues(initialRuleTestValues);
    setRuleTestResult(null);
    setEditingRuleId("");
  }

  async function handleTestRule() {
    try {
      setTestingRule(true);
      setMsg("");
      setRuleTestResult(null);

      const config = {};

      if (ruleForm.calculation_method === "fixed") {
        config.amount = Number(ruleForm.amount || 0);
      }

      if (ruleForm.calculation_method === "percentage") {
        config.percentage = Number(ruleForm.percentage || 0);
      }

      if (ruleForm.calculation_method === "progressive") {
        config.bands = safeParseBands(ruleForm.bands_json);
      }

      if (ruleForm.calculation_method === "formula") {
        config.formula = String(ruleForm.formula || "").trim();
        if (!config.formula) {
          throw new Error("Formula cannot be empty");
        }
      }

      const payload = {
        rule_type: ruleForm.rule_type,
        calculation_method: ruleForm.calculation_method,
        applies_to: ruleForm.applies_to,
        config,
        test_values: {
          gross: Number(ruleTestValues.gross || 0),
          basic: Number(ruleTestValues.basic || 0),
          taxable_gross: Number(ruleTestValues.taxable_gross || 0),
          pensionable_gross: Number(ruleTestValues.pensionable_gross || 0),
          net: Number(ruleTestValues.net || 0),
          regular_hours: Number(ruleTestValues.regular_hours || 0),
          overtime_hours: Number(ruleTestValues.overtime_hours || 0),
        },
      };

      const result = await testPayrollRule(payload);
      setRuleTestResult(result);
      setMsg("Rule test completed successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to test payroll rule");
    } finally {
      setTestingRule(false);
    }
  }

  async function handleEditRule(ruleId) {
    try {
      setLoadingRuleForEdit(true);
      setMsg("");

      const rule = await getStatutoryRuleById(ruleId);

      setEditingRuleId(rule.id);
      setRuleForm({
        preset_key: "",
        rule_code: rule.rule_code || "",
        rule_name: rule.rule_name || "",
        rule_type: rule.rule_type || "tax",
        calculation_method: rule.calculation_method || "percentage",
        applies_to: rule.applies_to || "gross",
        active: !!rule.active,
        effective_from: rule.effective_from || "",
        effective_to: rule.effective_to || "",
        percentage:
          rule.calculation_method === "percentage"
            ? String(rule.config?.percentage ?? "")
            : "",
        amount:
          rule.calculation_method === "fixed"
            ? String(rule.config?.amount ?? "")
            : "",
        bands_json:
          rule.calculation_method === "progressive"
            ? JSON.stringify(rule.config?.bands || [], null, 2)
            : "",
        formula:
          rule.calculation_method === "formula"
            ? String(rule.config?.formula || "")
            : "",
      });

      setRuleTestResult(null);
      setShowRuleModal(true);
    } catch (err) {
      setMsg(err.message || "Failed to load statutory rule for editing");
    } finally {
      setLoadingRuleForEdit(false);
    }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();

    try {
      setMsg("");

      await upsertEmployeeStatutoryProfile({
        company_id: companyId,
        employee_id: profileForm.employee_id,
        tax_state: profileForm.tax_state || null,
        tax_residency: profileForm.tax_residency || null,
        pension_exempt: !!profileForm.pension_exempt,
        tax_exempt: !!profileForm.tax_exempt,
        nhf_exempt: !!profileForm.nhf_exempt,
        nhis_exempt: !!profileForm.nhis_exempt,
        custom_config: {},
      });

      setProfileForm(initialProfileForm);
      setShowProfileModal(false);
      await load();
      setMsg("Employee statutory profile saved successfully.");
    } catch (err) {
      setMsg(err.message || "Failed to save employee profile");
    }
  }

  function handlePresetChange(presetKey) {
    setRuleForm((prev) => ({
      ...prev,
      ...presetToForm(presetKey),
      effective_from: prev.effective_from || "",
      effective_to: prev.effective_to || "",
      active: true,
    }));

    setRuleTestResult(null);
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert info">{msg}</div> : null}

      <div className="payroll-grid payroll-grid-4">
        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Statutory Rules</p>
          <h3>{rules.length}</h3>
          <span>Configured compliance rules</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Active Rules</p>
          <h3>{activeRules}</h3>
          <span>Rules currently in force</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Employee Profiles</p>
          <h3>{profiles.length}</h3>
          <span>Employee-specific statutory settings</span>
        </div>

        <div className="payroll-card payroll-summary-card">
          <p className="payroll-summary-title">Exempt Profiles</p>
          <h3>{exemptProfiles}</h3>
          <span>Employees with at least one exemption</span>
        </div>
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>Compliance Configuration</h3>
            <p>
              Set up statutory rules and employee-level statutory exemptions.
            </p>
          </div>

          <div className="table-actions">
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                resetRuleModalState();
                setShowRuleModal(true);
              }}
            >
              <Plus size={16} />
              Add Statutory Rule
            </button>

            <button
              type="button"
              className="primary-btn"
              onClick={() => setShowProfileModal(true)}
            >
              <Plus size={16} />
              Add Employee Profile
            </button>
          </div>
        </div>

        <div className="payroll-grid payroll-grid-2">
          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Statutory Rules</h3>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Method</th>
                    <th>Applies To</th>
                    <th>Effective From</th>
                    <th>Status</th>
                    <th>Action</th>
                    <th>Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {rules.map((row) => (
                    <tr key={row.id}>
                      <td>{row.rule_code}</td>
                      <td>{row.rule_name}</td>
                      <td>{row.rule_type}</td>
                      <td>{row.calculation_method}</td>
                      <td>{row.applies_to}</td>
                      <td>{row.effective_from}</td>
                      <td>
                        <span
                          className={`pill ${
                            row.active ? "active" : "inactive"
                          }`}
                        >
                          {row.active ? "active" : "inactive"}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={row.active ? "ghost-btn" : "primary-btn"}
                          onClick={() => handleToggleRule(row)}
                        >
                          {row.active ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="ghost-btn"
                          onClick={() => handleEditRule(row.id)}
                          disabled={loadingRuleForEdit}
                        >
                          <Pencil size={14} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!rules.length && !busy && (
                    <tr>
                      <td colSpan="9">No statutory rules found.</td>
                    </tr>
                  )}
                  {busy && (
                    <tr>
                      <td colSpan="9">Loading...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Employee Statutory Profiles</h3>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Tax State</th>
                    <th>Residency</th>
                    <th>Tax Exempt</th>
                    <th>Pension Exempt</th>
                    <th>NHF Exempt</th>
                    <th>NHIS Exempt</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((row) => {
                    const employee = pickOne(row.payroll_employees);

                    return (
                      <tr key={row.id}>
                        <td>
                          {employee
                            ? `${employee.employee_code} - ${employee.first_name} ${employee.last_name}`
                            : "-"}
                        </td>
                        <td>{row.tax_state || "-"}</td>
                        <td>{row.tax_residency || "-"}</td>
                        <td>{row.tax_exempt ? "Yes" : "No"}</td>
                        <td>{row.pension_exempt ? "Yes" : "No"}</td>
                        <td>{row.nhf_exempt ? "Yes" : "No"}</td>
                        <td>{row.nhis_exempt ? "Yes" : "No"}</td>
                      </tr>
                    );
                  })}
                  {!profiles.length && !busy && (
                    <tr>
                      <td colSpan="7">No employee statutory profiles found.</td>
                    </tr>
                  )}
                  {busy && (
                    <tr>
                      <td colSpan="7">Loading...</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showRuleModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>
                {editingRuleId
                  ? "Edit Statutory Rule"
                  : "Create Statutory Rule"}
              </h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  setShowRuleModal(false);
                  setRuleTestResult(null);
                  setRuleTestValues(initialRuleTestValues);
                  resetRuleModalState();
                }}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleSaveRule}>
              <div className="payroll-card compliance-help-box">
                <div className="compliance-help-title">
                  <Info size={16} />
                  <span>Quick Start Presets</span>
                </div>

                <div className="payroll-grid payroll-grid-2">
                  <label>
                    Preset
                    <select
                      value={ruleForm.preset_key}
                      onChange={(e) => handlePresetChange(e.target.value)}
                    >
                      <option value="">
                        Select a preset or create manually
                      </option>
                      {Object.entries(PRESETS).map(([key, preset]) => (
                        <option key={key} value={key}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="compliance-help-note">
                    <Wand2 size={15} />
                    <span>
                      Presets auto-fill the form. You can still edit values
                      before saving.
                    </span>
                  </div>
                </div>
              </div>

              <div className="payroll-grid payroll-grid-2">
                <label>
                  Rule Code
                  <input
                    value={ruleForm.rule_code}
                    onChange={(e) =>
                      setRuleForm((p) => ({ ...p, rule_code: e.target.value }))
                    }
                    required
                  />
                </label>

                <label>
                  Rule Name
                  <input
                    value={ruleForm.rule_name}
                    onChange={(e) =>
                      setRuleForm((p) => ({ ...p, rule_name: e.target.value }))
                    }
                    required
                  />
                </label>

                <label>
                  Rule Type
                  <select
                    value={ruleForm.rule_type}
                    onChange={(e) =>
                      setRuleForm((p) => ({ ...p, rule_type: e.target.value }))
                    }
                  >
                    <option value="tax">Tax</option>
                    <option value="pension_employee">Pension Employee</option>
                    <option value="pension_employer">Pension Employer</option>
                    <option value="nhf">NHF</option>
                    <option value="nhis">NHIS</option>
                    <option value="other">Other</option>
                  </select>
                </label>

                <label>
                  Calculation Method
                  <select
                    value={ruleForm.calculation_method}
                    onChange={(e) =>
                      setRuleForm((p) => ({
                        ...p,
                        calculation_method: e.target.value,
                      }))
                    }
                  >
                    <option value="fixed">Fixed</option>
                    <option value="percentage">Percentage</option>
                    <option value="progressive">Progressive</option>
                    <option value="formula">Formula</option>
                  </select>
                </label>

                <label>
                  Applies To
                  <select
                    value={ruleForm.applies_to}
                    onChange={(e) =>
                      setRuleForm((p) => ({ ...p, applies_to: e.target.value }))
                    }
                  >
                    <option value="gross">Gross</option>
                    <option value="basic">Basic</option>
                    <option value="taxable_gross">Taxable Gross</option>
                    <option value="pensionable_gross">Pensionable Gross</option>
                    <option value="custom">Custom</option>
                  </select>
                </label>

                <label>
                  Effective From
                  <input
                    type="date"
                    value={ruleForm.effective_from}
                    onChange={(e) =>
                      setRuleForm((p) => ({
                        ...p,
                        effective_from: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Effective To
                  <input
                    type="date"
                    value={ruleForm.effective_to}
                    onChange={(e) =>
                      setRuleForm((p) => ({
                        ...p,
                        effective_to: e.target.value,
                      }))
                    }
                  />
                </label>

                {ruleForm.calculation_method === "fixed" ? (
                  <label>
                    Fixed Amount
                    <input
                      type="number"
                      step="0.01"
                      value={ruleForm.amount}
                      onChange={(e) =>
                        setRuleForm((p) => ({ ...p, amount: e.target.value }))
                      }
                    />
                  </label>
                ) : null}

                {ruleForm.calculation_method === "percentage" ? (
                  <label>
                    Percentage
                    <input
                      type="number"
                      step="0.0001"
                      value={ruleForm.percentage}
                      onChange={(e) =>
                        setRuleForm((p) => ({
                          ...p,
                          percentage: e.target.value,
                        }))
                      }
                    />
                  </label>
                ) : null}
              </div>

              {ruleForm.calculation_method === "progressive" ? (
                <div className="payroll-card compliance-help-box">
                  <div className="compliance-help-title">
                    <Info size={16} />
                    <span>Progressive JSON Help</span>
                  </div>

                  <div className="compliance-help-list">
                    <div>
                      Supported keys are: <code>up_to</code>, <code>next</code>,
                      and <code>over</code>.
                    </div>
                    <div>
                      <code>up_to</code> means cumulative threshold.
                    </div>
                    <div>
                      <code>next</code> means band width from the previous band.
                    </div>
                    <div>
                      <code>over</code> means everything above that threshold.
                    </div>
                  </div>

                  <label>
                    Bands JSON
                    <textarea
                      rows="9"
                      value={ruleForm.bands_json}
                      onChange={(e) =>
                        setRuleForm((p) => ({
                          ...p,
                          bands_json: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <div className="compliance-example-box">
                    <strong>Nigeria PIT stepped example</strong>
                    <pre>{JSON.stringify(NIGERIA_PIT_PRESET, null, 2)}</pre>
                  </div>
                </div>
              ) : null}

              {ruleForm.calculation_method === "formula" ? (
                <div className="payroll-card compliance-help-box">
                  <div className="compliance-help-title">
                    <Info size={16} />
                    <span>Formula Help</span>
                  </div>

                  <div className="compliance-help-list">
                    <div>
                      Available variables: <code>gross</code>,{" "}
                      <code>basic</code>, <code>taxable_gross</code>,{" "}
                      <code>pensionable_gross</code>, <code>net</code>,{" "}
                      <code>regular_hours</code>, <code>overtime_hours</code>.
                    </div>
                    <div>
                      Available functions: <code>min()</code>,{" "}
                      <code>max()</code>, <code>abs()</code>,{" "}
                      <code>round()</code>, <code>floor()</code>,{" "}
                      <code>ceil()</code>.
                    </div>
                    <div>
                      Allowed operators: <code>+</code>, <code>-</code>,{" "}
                      <code>*</code>, <code>/</code>, and parentheses.
                    </div>
                  </div>

                  <label>
                    Formula
                    <textarea
                      rows="4"
                      placeholder="Example: min(gross * 0.05, 500000)"
                      value={ruleForm.formula}
                      onChange={(e) =>
                        setRuleForm((p) => ({ ...p, formula: e.target.value }))
                      }
                    />
                  </label>

                  <div className="compliance-example-box">
                    <strong>Example formulas</strong>
                    <pre>{`pensionable_gross * 0.08
basic * 0.025
min(gross * 0.05, 500000)
max((taxable_gross - 300000) * 0.1, 0)`}</pre>
                  </div>
                </div>
              ) : null}

              <div className="payroll-card compliance-help-box">
                <div className="compliance-help-title">
                  <Beaker size={16} />
                  <span>Rule Test Engine</span>
                </div>

                <div className="compliance-help-list">
                  <div>
                    Enter sample values below to test this rule before saving
                    it.
                  </div>
                  <div>
                    This helps users confirm percentages, progressive tax bands,
                    and formulas.
                  </div>
                </div>

                <div className="payroll-grid payroll-grid-2">
                  <label>
                    Gross
                    <input
                      type="number"
                      step="0.01"
                      value={ruleTestValues.gross}
                      onChange={(e) =>
                        setRuleTestValues((p) => ({
                          ...p,
                          gross: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Basic
                    <input
                      type="number"
                      step="0.01"
                      value={ruleTestValues.basic}
                      onChange={(e) =>
                        setRuleTestValues((p) => ({
                          ...p,
                          basic: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Taxable Gross
                    <input
                      type="number"
                      step="0.01"
                      value={ruleTestValues.taxable_gross}
                      onChange={(e) =>
                        setRuleTestValues((p) => ({
                          ...p,
                          taxable_gross: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Pensionable Gross
                    <input
                      type="number"
                      step="0.01"
                      value={ruleTestValues.pensionable_gross}
                      onChange={(e) =>
                        setRuleTestValues((p) => ({
                          ...p,
                          pensionable_gross: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Net
                    <input
                      type="number"
                      step="0.01"
                      value={ruleTestValues.net}
                      onChange={(e) =>
                        setRuleTestValues((p) => ({
                          ...p,
                          net: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Regular Hours
                    <input
                      type="number"
                      step="0.01"
                      value={ruleTestValues.regular_hours}
                      onChange={(e) =>
                        setRuleTestValues((p) => ({
                          ...p,
                          regular_hours: e.target.value,
                        }))
                      }
                    />
                  </label>

                  <label>
                    Overtime Hours
                    <input
                      type="number"
                      step="0.01"
                      value={ruleTestValues.overtime_hours}
                      onChange={(e) =>
                        setRuleTestValues((p) => ({
                          ...p,
                          overtime_hours: e.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <div className="payroll-form-actions">
                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={handleTestRule}
                    disabled={testingRule}
                  >
                    <Beaker size={16} />
                    {testingRule ? "Testing..." : "Test Rule"}
                  </button>
                </div>

                {ruleTestResult ? (
                  <div className="compliance-test-result">
                    <div className="payroll-grid payroll-grid-3">
                      <div className="payroll-mini-stat">
                        <strong>Calculation Base</strong>
                        <span>
                          {Number(
                            ruleTestResult.calculation_base || 0
                          ).toLocaleString()}
                        </span>
                      </div>

                      <div className="payroll-mini-stat">
                        <strong>Calculated Amount</strong>
                        <span>
                          {Number(ruleTestResult.amount || 0).toLocaleString()}
                        </span>
                      </div>

                      <div className="payroll-mini-stat">
                        <strong>Method</strong>
                        <span>{ruleTestResult.calculation_method || "-"}</span>
                      </div>
                    </div>

                    {ruleTestResult.formula_used ? (
                      <div className="compliance-example-box">
                        <strong>Formula Used</strong>
                        <pre>{ruleTestResult.formula_used}</pre>
                      </div>
                    ) : null}

                    {Array.isArray(ruleTestResult.progressive_breakdown) &&
                    ruleTestResult.progressive_breakdown.length ? (
                      <div
                        className="payroll-table-wrap"
                        style={{ marginTop: 14 }}
                      >
                        <table className="payroll-table">
                          <thead>
                            <tr>
                              <th>Band</th>
                              <th>Rate %</th>
                              <th>Taxable Amount</th>
                              <th>Tax Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ruleTestResult.progressive_breakdown.map(
                              (band, idx) => (
                                <tr key={`${band.label}-${idx}`}>
                                  <td>{band.label}</td>
                                  <td>
                                    {Number(band.rate || 0).toLocaleString()}
                                  </td>
                                  <td>
                                    {Number(
                                      band.taxable_amount || 0
                                    ).toLocaleString()}
                                  </td>
                                  <td>
                                    {Number(
                                      band.tax_amount || 0
                                    ).toLocaleString()}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <label className="check-inline">
                <input
                  type="checkbox"
                  checked={ruleForm.active}
                  onChange={(e) =>
                    setRuleForm((p) => ({ ...p, active: e.target.checked }))
                  }
                />
                Active Rule
              </label>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => {
                    setShowRuleModal(false);
                    setRuleTestResult(null);
                    setRuleTestValues(initialRuleTestValues);
                    resetRuleModalState();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  {editingRuleId ? "Update Rule" : "Save Rule"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showProfileModal ? (
        <div className="payroll-modal-overlay">
          <div className="payroll-modal">
            <div className="payroll-modal-header">
              <h3>Create Employee Statutory Profile</h3>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => setShowProfileModal(false)}
              >
                Close
              </button>
            </div>

            <form className="payroll-form" onSubmit={handleSaveProfile}>
              <div className="payroll-grid payroll-grid-2">
                <label>
                  Employee
                  <select
                    value={profileForm.employee_id}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        employee_id: e.target.value,
                      }))
                    }
                    required
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.employee_code} - {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Tax State
                  <input
                    value={profileForm.tax_state}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        tax_state: e.target.value,
                      }))
                    }
                  />
                </label>

                <label>
                  Tax Residency
                  <input
                    value={profileForm.tax_residency}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        tax_residency: e.target.value,
                      }))
                    }
                  />
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={profileForm.tax_exempt}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        tax_exempt: e.target.checked,
                      }))
                    }
                  />
                  Tax Exempt
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={profileForm.pension_exempt}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        pension_exempt: e.target.checked,
                      }))
                    }
                  />
                  Pension Exempt
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={profileForm.nhf_exempt}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        nhf_exempt: e.target.checked,
                      }))
                    }
                  />
                  NHF Exempt
                </label>

                <label className="check-inline">
                  <input
                    type="checkbox"
                    checked={profileForm.nhis_exempt}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        nhis_exempt: e.target.checked,
                      }))
                    }
                  />
                  NHIS Exempt
                </label>
              </div>

              <div className="payroll-form-actions">
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => setShowProfileModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-btn">
                  Save Profile
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
